import { holidaysInPTtmzLuxon, isWeekday } from 'components/generic-components/datepicker/Datepicker';
import { DateTime } from 'luxon';
import { getPlural } from './string';

export const convertDateToFormat = (date: string | number, format: string) => {
  const dateValue = typeof date === 'number' ? date * 1000 : date;
  const innerDateObject = new Date(dateValue);

  return DateTime.fromISO(innerDateObject.toISOString()).toFormat(format);
};

/** Short month label for a chart axis, e.g. "Sep". */
export const formatMonthShort = (month: DateTime): string => month.toLocaleString({ month: 'short' });

/** Month and year for a chart's range caption, e.g. "Sep 2026". */
export const formatMonthAndYear = (month: DateTime): string =>
  month.toLocaleString({ month: 'short', year: 'numeric' });

/** e.g. "March 2026" */
export const formatMonthLong = (month: DateTime): string => month.toLocaleString({ month: 'long', year: 'numeric' });

export const invoiceDateFormat = 'MMMM dd, yyyy';
export const defaultDateFormat = 'MMMM dd, yyyy';
export const defaultDateAndTimeFormat = 'MMMM dd, yyyy, h:mm a';

/** Short enough to sit in a table column without pushing the useful columns off screen. */
export const compactDateAndTimeFormat = 'dd LLL yyyy, h:mm a';
export const compactDateFormat = 'dd LLL yyyy';

/** e.g. "7:13 PM", for a time shown under its date */
export const timeOfDayFormat = 'h:mm a';

/** e.g. "Sep 28, 2026, 3:44 AM" */
export const shortDateAndTimeFormat = 'LLL d, yyyy, h:mm a';

/** e.g. "22 Dec, 2026" */
export const dayMonthYearFormat = 'd LLL, yyyy';

/** e.g. "22 Dec", for the start of a range whose end already shows the year */
export const dayMonthFormat = 'd LLL';

/**
 * Formats an epoch timestamp in milliseconds. Records that store a raw number rather than an
 * ISO string come through here so they read the same as every other date.
 */
export const epochTimestampToDateAndTime = (
  timestamp: number | undefined | null,
  format = defaultDateAndTimeFormat,
): string => {
  if (timestamp === undefined || timestamp === null) {
    return 'Invalid date';
  }
  const date = DateTime.fromMillis(timestamp);
  return date.isValid ? date.toFormat(format) : 'Invalid date';
};

/**
 * Convert a timestamp to a date string
 * @param {number} timestamp - The timestamp to convert (default: current timestamp)
 * @param {string} format - The format to return the date in (default: yyyy-MM-dd)
 * @returns {string} - The date string in the desired format e.g. 2020-01-01
 */
export const timestampToDateString = ({
  timestamp = DateTime.now().toSeconds(),
  format = 'yyyy-MM-dd',
}: {
  timestamp?: number;
  format?: string;
}): string => {
  // Convert the timestamp to a DateTime object
  const date = DateTime.fromSeconds(timestamp);
  // Return the date in the desired format
  return date.toFormat(format);
};

/**
 * Convert a date string to a date string in the format used in invoices
 * @param {string} dateString - The date string to convert e.g. 2020-01-01
 * @returns {string} - The date string in the desired format e.g. January 01, 2020
 * @example dateStringToinvoiceDateFormat('2020-01-01') // January 01, 2020
 */
export const dateStringToinvoiceDateFormat = (
  dateString: string | undefined | null,
  format = invoiceDateFormat,
): string => {
  // Handle undefined, null, or empty string
  if (!dateString) {
    return 'Invalid date';
  }

  // Convert the timestamp to a DateTime object
  const date = DateTime.fromISO(dateString);

  // Check if the date is valid
  if (!date.isValid) {
    return 'Invalid date';
  }

  // Return the date in the desired format
  return date.toFormat(format);
};

/**
 * Convert a date string to a date string in the format specified
 * @param {string} dateString - The date string to convert e.g. 2020-01-01
 * @returns {string} - The date string in the desired format e.g. January 01, 2020
 * @example dateStringToSpecifiedDateFormat('2020-01-01') // January 01, 2020
 */
export const dateStringToSpecifiedDateFormat = (
  dateString: string | undefined | null,
  format = defaultDateFormat,
): string => {
  // Handle undefined, null, or empty string
  if (!dateString) {
    return 'Invalid date';
  }

  // Convert the timestamp to a DateTime object
  const date = DateTime.fromISO(dateString);

  // Check if the date is valid
  if (!date.isValid) {
    return 'Invalid date';
  }

  // Return the date in the desired format
  return date.toFormat(format);
};

export const dateStringToSpecifiedDateAndTimeFormat = (
  dateString: string | undefined | null,
  format = defaultDateAndTimeFormat,
): string => {
  // Handle undefined, null, or empty string
  if (!dateString) {
    return 'Invalid date';
  }

  // Convert the timestamp to a DateTime object
  const date = DateTime.fromISO(dateString);

  // Check if the date is valid
  if (!date.isValid) {
    return 'Invalid date';
  }

  // Return the date in the desired format
  return date.toFormat(format);
};

/**
 * Calculate the due date for an invoice
 * @param {string} startDate - The start date of the invoice e.g. 2020-01-01
 * @param {number} noOfAdvanceDays - The number of days to add to the start date (default: 7)
 * @param {string} format - The format to return the date in (default: yyyy-MM-dd)
 * @returns {string} - The due date in the desired format e.g. 2020-01-08
 */
export const calculateDueDate = ({
  startDateTimestamp = DateTime.now().toSeconds(),
  noOfAdvanceDays = 7,
  format = 'yyyy-MM-dd',
}): string => {
  // Parse the input string to DateTime object
  // const start = DateTime.fromFormat(startDate, 'yyyy-MM-dd');
  const start = DateTime.fromSeconds(startDateTimestamp);
  // Add 7 days to the start date
  const dueDate = start.plus({ days: noOfAdvanceDays });
  // Return the due date in the desired format
  return dueDate.toFormat(format);
};

export const dateOrdinalSuffix = (date: string | undefined | null): 'st' | 'nd' | 'rd' | 'th' => {
  // Handle undefined, null, or empty string
  if (!date) {
    return 'th';
  }

  // convert the ISO date string to a DateTime object
  const dateObject = DateTime.fromISO(date);

  // Check if the date is valid
  if (!dateObject.isValid) {
    return 'th';
  }

  const day = dateObject.day;
  switch (day) {
    case 1:
    case 21:
    case 31:
      return 'st';
    case 2:
    case 22:
      return 'nd';
    case 3:
    case 23:
      return 'rd';
    default:
      return 'th';
  }
};

export function formatWithOrdinal(date: DateTime): string {
  const day = date.day;
  let ordinal = '';
  switch (day) {
    case 1:
    case 21:
    case 31:
      ordinal = 'st';
      break;
    case 2:
    case 22:
      ordinal = 'nd';
      break;
    case 3:
    case 23:
      ordinal = 'rd';
      break;
    default:
      ordinal = 'th';
  }

  return `${date.toFormat('MMM')} ${day}${ordinal}`;
}

export function getOrdinalSuffix(n: number): string {
  if (n > 3 && n < 21) return 'th';
  switch (n % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

export const countDaysBetween = (startDate: string, endDate: string, includeWeekends = false) => {
  let start = DateTime.fromISO(startDate);
  let end = DateTime.fromISO(endDate);

  if (end < start) {
    [start, end] = [end, start]; // Swap if end is earlier than start
  }

  let days = 0;
  while (start <= end) {
    const dayOfWeek = start.weekday;

    // If includeWeekends is true, increment days for all days
    // Else, only increment for weekdays (Monday is 1, Friday is 5)
    if (includeWeekends || (dayOfWeek >= 1 && dayOfWeek <= 5)) {
      days++;
    }

    start = start.plus({ days: 1 });
  }

  return days;
};

// Function to convert ISO date string to a human-readable format
export function getRelativeTime(isoDateString: string): string {
  const now = DateTime.now();
  const targetDate = DateTime.fromISO(isoDateString);

  const diff = targetDate.diff(now, ['years', 'months', 'days']).toObject();

  // Round the days to the nearest whole number
  const roundedDays = Math.round(diff.days) + 1;

  if (diff.years > 0) {
    return `${diff.years} year${diff.years > 1 ? 's' : ''}`;
  } else if (diff.months > 0) {
    return `${diff.months} month${diff.months > 1 ? 's' : ''}`;
  } else if (roundedDays > 0) {
    return `${roundedDays} day${roundedDays > 1 ? 's' : ''}`;
  } else {
    return 'Today';
  }
}

export function getRelativeDaysString(dateString: string | undefined | null): string {
  // Handle undefined, null, or empty string
  if (!dateString) {
    return 'date unavailable';
  }

  const targetDate = DateTime.fromISO(dateString).startOf('day');

  // Check if the date is valid
  if (!targetDate.isValid) {
    return 'date unavailable';
  }

  const currentDate = DateTime.now().startOf('day');
  const diffInDays = targetDate.diff(currentDate, 'days').days;

  if (diffInDays < 0) {
    const countOfDays = Math.abs(Math.floor(diffInDays));
    return `${countOfDays} ${getPlural(countOfDays, 'day')} ago`;
  } else if (diffInDays === 0) {
    return 'today';
  } else if (diffInDays === 1) {
    return 'tomorrow';
  } else {
    return `${Math.floor(diffInDays)} days`;
  }
}

export const getTaxYear = (): number => {
  const currentDate = DateTime.now();
  const year = currentDate.year;

  // Check if the current date is between January 25 and December 31 of the current year
  if (currentDate >= DateTime.local(year, 6, 29) && currentDate <= DateTime.local(year, 12, 31)) {
    return year;
  } else {
    return year - 1;
  }
};

export const checkIfContractIsEndingIn7Days = (contractEndDate: string): boolean => {
  if (!contractEndDate) return false;
  const todayDateInUTC = DateTime.now().toUTC().toISODate();
  const differenceInDays = countDaysBetween(todayDateInUTC, contractEndDate);
  const isContractEndingIn7Days = differenceInDays <= 7 && differenceInDays > 0;
  return isContractEndingIn7Days;
};

/**
 * Function to check if a date is in the past */
export function isDateInPast(isoDateString: string): boolean {
  // we were previously using startOf('day') here which was resulting in error as for now time it was taking midnight time and same for target time.
  // the now time we get is of UTC of users time, for eg :- if its 12 April and Time is 18 hrs 30 mins of India time, give its UTC+5.30
  // the now = DateTime.utc() will be 12 April and Time will be 13 hrs 0 mins.
  // for targetDate we get a string and covert it to zone utc, if we get 12 april and zone is utc, as we are not getting time,
  // it will be converted to midnight, so targetDate will be 12 April, 0 hrs, 0 mins
  // condition is targetDate < now, which is for eg. 12 April, 0 hrs, 0 mins < 12 April, 13 hrs, 0 mins.
  // means it is and contract will end immediately.
  if (!isoDateString) return false;
  const now = DateTime.utc();
  const targetDate = DateTime.fromISO(isoDateString, { zone: 'utc' });
  return targetDate < now;
}

/* Function to check if a date is a weekend (Saturday or Sunday) */
export const isWeekend = (date: DateTime) => {
  const day = date.weekday;
  // In Luxon, weekday is 1 (Monday) - 7 (Sunday)
  return day === 6 || day === 7; // 6 is Saturday, 7 is Sunday
};

const holidays = holidaysInPTtmzLuxon.map((date) => DateTime.fromJSDate(date));

/* Function to check if a date is a holiday */
export const isNotHoliday = (date: DateTime) => {
  const format = 'yyyy-MM-dd';
  return !holidays.some((holiday) => holiday.toFormat(format) === date.toFormat(format));
};

// Find the nth (default 3rd) working day (weekday) from today, exclude weekends and holidays
export const getFirstAvailablePayDate = (daysAfter = 3) => {
  let currentDate = DateTime.now().setZone('America/Los_Angeles').startOf('day');
  let workingDaysCount = 0;

  // Loop until we find the 3rd working day
  while (workingDaysCount < daysAfter) {
    currentDate = currentDate.plus({ days: 1 });
    // Check if it's a working day
    if (!isWeekend(currentDate) && isNotHoliday(currentDate)) {
      workingDaysCount++;
    }
  }

  return currentDate.toUTC().toJSDate();
};

/**
 * Check requires a company's payroll start date to be at least N business days out (10 by default).
 * Payroll onboarding spans several days (bank setup, migration, etc.), so a date chosen earlier in
 * the flow can roll inside that window and get rejected by Check later. Use this to detect a stale
 * start date (ISO `yyyy-MM-dd`) before submitting, so we can re-prompt instead of failing.
 */
export const isStartDateTooEarly = (isoDate?: string | null, minBusinessDays = 10): boolean => {
  if (!isoDate) return false;
  const provided = DateTime.fromISO(isoDate).toISODate();
  const earliestValid = DateTime.fromJSDate(getFirstAvailablePayDate(minBusinessDays)).toISODate();
  if (!provided || !earliestValid) return false;
  return provided < earliestValid;
};

export function getHumanReadableDate(inputDate: Date): string {
  if (!inputDate) {
    return 'Invalid date';
  }

  // Convert inputDate to Luxon DateTime object and specify it's in UTC
  const inputDateTime = DateTime.fromJSDate(inputDate, { zone: 'utc' });

  // Get current date in UTC
  const currentDate = DateTime.utc();

  // Calculate the difference in days and hours between currentDate and inputDateTime
  const { days, hours } = inputDateTime.diff(currentDate, ['days', 'hours']).toObject();

  if (days === 0 && hours >= -24 && hours < 0) {
    return 'Today';
  } else if (days === 0 && hours >= 0 && hours < 24) {
    return 'Today'; // If falls within the next 24 hours
  } else if (days === 1) {
    return 'Tomorrow';
  } else if (days > 1) {
    return `In ${days} days`;
  } else {
    // If inputDateTime is in the past, return date in format "18 Apr, 2024"
    return inputDateTime.toFormat('dd LLL, yyyy');
  }
}

// Function to check if a date is in the future (starting from tomorrow)
export function isInFuture(date: Date) {
  // Convert the input date object to a Luxon DateTime object
  const luxonDate = DateTime.fromJSDate(date, { zone: 'utc' });

  // Get tomorrow's date
  const tomorrow = DateTime.utc().plus({ days: 1 }).startOf('day');

  // Check if the input date is after tomorrow
  return luxonDate >= tomorrow;
}

export const getTimeDifference = (startTimestamp: number, endTimestamp: number): string => {
  const diffInSeconds = endTimestamp - startTimestamp;

  const hours = Math.floor(diffInSeconds / 3600);
  const minutes = Math.floor((diffInSeconds % 3600) / 60);
  const seconds = diffInSeconds % 60;

  return `${hours} hour${hours !== 1 ? 's' : ''}, ${minutes} minute${minutes !== 1 ? 's' : ''}, and ${seconds} second${seconds !== 1 ? 's' : ''}`;
};

export const timestampToLocalDateAndTime = ({
  timestamp = DateTime.now().toSeconds(),
}: {
  timestamp?: number;
}): { date: string; time: string } => {
  const localDateTime = DateTime.fromSeconds(timestamp).setZone(DateTime.local().zoneName);

  return {
    date: localDateTime.toFormat('yyyy-MM-dd'),
    time: localDateTime.toFormat('h:mm a'),
  };
};

/**
 * Adds one business day to the given date, skipping weekends and US bank holidays
 * @param date DateTime object to add one business day to
 * @returns DateTime object representing the next business day
 */
export const addOneBusinessDay = (date: DateTime): DateTime => {
  let nextDay = date;

  // Keep advancing until we find a business day that's not a holiday
  while (
    !isWeekday(nextDay.toJSDate()) ||
    holidaysInPTtmzLuxon.some((holiday) => DateTime.fromJSDate(holiday).toISODate() === nextDay.toISODate())
  ) {
    nextDay = nextDay.plus({ days: 1 });
  }

  return nextDay;
};

/**
 * Returns true if the given date is a US business day (weekday and not a US bank holiday).
 *
 * Note: We rely on `holidaysInPTtmzLuxon` (shared app-wide) and compare by ISO date to avoid
 * time-of-day differences. We also set the holiday to the same zone as the input date to reduce
 * timezone edge cases.
 */
export const isBusinessDay = (date: DateTime): boolean => {
  const isHoliday = holidaysInPTtmzLuxon.some((holiday) => {
    const holidayISODate = DateTime.fromJSDate(holiday).setZone(date.zoneName).toISODate();
    return holidayISODate === date.toISODate();
  });

  return isWeekday(date.toJSDate()) && !isHoliday;
};

/**
 * Returns the next business day after the given date (strictly in the future).
 * Skips weekends and US bank holidays.
 */
export const getNextBusinessDay = (date: DateTime): DateTime => {
  // Start searching from tomorrow (the "next" business day must be in the future).
  let candidate = date.plus({ days: 1 }).startOf('day');

  while (!isBusinessDay(candidate)) {
    candidate = candidate.plus({ days: 1 });
  }

  return candidate;
};

/**
 * Returns the current business day if `date` is a business day; otherwise returns the next one.
 * This is useful when a user action can happen on weekends/holidays but processing only happens
 * on business days.
 */
export const getCurrentOrNextBusinessDay = (date: DateTime): DateTime => {
  let candidate = date.startOf('day');

  while (!isBusinessDay(candidate)) {
    candidate = candidate.plus({ days: 1 });
  }

  return candidate;
};

/**
 * Payment modal "estimated processing date" logic.
 *
 * Business rule:
 * - If payment is made **after 2 PM ET**, processing is estimated for the **following business day**
 * - Otherwise, processing is estimated for the **current business day**
 *
 * Edge cases:
 * - If "today" is not a business day (weekend/holiday), we estimate the next business day regardless of time.
 *
 * We use "America/New_York" (ET) to correctly handle daylight savings time.
 */
export const getEstimatedPayInsProcessingBusinessDateET = (now: DateTime = DateTime.now()): DateTime => {
  const nowET = now.setZone('America/New_York');
  const cutoffHourET = 14; // 2 PM ET

  // If today isn't a business day, we can't process today.
  const currentOrNextBusinessDayET = getCurrentOrNextBusinessDay(nowET);
  const isTodayBusinessDayET = currentOrNextBusinessDayET.toISODate() === nowET.startOf('day').toISODate();

  if (!isTodayBusinessDayET) {
    return currentOrNextBusinessDayET;
  }

  // Today is a business day: after cutoff -> next business day, otherwise today.
  return nowET.hour >= cutoffHourET ? getNextBusinessDay(nowET) : nowET.startOf('day');
};

export const calculateIncorporationDetails = (registrationDate: string, status: string, updatedAt: string) => {
  const startDate = DateTime.fromISO(registrationDate);
  const endDate = startDate.plus({ days: 10 }); // 10 days from registration date
  const today = DateTime.now().setZone(startDate.zone);

  const totalDays = 10; // Fixed value since we're adding 10 days

  const startDateDay = startDate.startOf('day');
  const todayDay = today.startOf('day');

  // Calculate difference in days directly
  const elapsedDays = todayDay.diff(startDateDay, 'days').days;

  const remainingDays = Math.max(0, totalDays - elapsedDays);

  // Handle updated date - convert from UTC to local time if available
  let formattedCompletionDate;
  if (updatedAt) {
    // Convert UTC string to DateTime object and then to local time
    const updatedDateTime = DateTime.fromISO(updatedAt).toLocal();
    formattedCompletionDate = updatedDateTime.toFormat('MMM dd, yyyy');
  } else {
    formattedCompletionDate = endDate.toFormat('MMM dd, yyyy');
  }

  if (!registrationDate || status === 'complete') {
    return {
      progressPercentage: 100,
      remainingDays: 0,
      estimatedCompletionDate: formattedCompletionDate || 'Unknown',
    };
  }

  const hasPassedCompletionDate = today > endDate;

  let progressPercentage = 0;

  // If we've passed the completion date but status isn't complete, cap at 99%
  if (hasPassedCompletionDate && status !== 'complete') {
    progressPercentage = 99;
  } else if (elapsedDays >= totalDays) {
    // If we've reached the total days but haven't passed the completion date
    progressPercentage = 100;
  } else if (elapsedDays > 0) {
    // Normal progress calculation
    progressPercentage = Math.min(100, Math.round((elapsedDays / totalDays) * 100));
  }

  return {
    progressPercentage,
    remainingDays,
    estimatedCompletionDate: formattedCompletionDate,
  };
};

export const formatHolidayDateRange = (startDate?: string, endDate?: string): string => {
  if (!startDate || !endDate) return 'N/A';

  try {
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Check if dates are valid
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 'N/A';

    const formatDate = (date: Date): string => {
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
      });
    };

    // If start and end dates are the same
    if (start.toDateString() === end.toDateString()) {
      return formatDate(start);
    }

    // If different dates
    return `${formatDate(start)} - ${formatDate(end)}`;
  } catch {
    return 'N/A';
  }
};

/**
 * Format card expiry date from ISO format (YYYY-MM-DD) to MM/YY format
 * @param {string} expiryDate - The expiry date in ISO format e.g. "2030-12-11"
 * @returns {string} - The expiry date in MM/YY format e.g. "12/30"
 * @example formatCardExpiryDate('2030-12-11') // "12/30"
 */
export const formatCardExpiryDate = (expiryDate: string | undefined | null): string => {
  // Handle undefined, null, or empty string
  if (!expiryDate) {
    return 'N/A';
  }

  try {
    // Parse the ISO date string to DateTime object
    const date = DateTime.fromISO(expiryDate);

    // Check if the date is valid
    if (!date.isValid) {
      return 'N/A';
    }

    // Extract month and year, format as MM/YY
    const month = date.toFormat('MM');
    const year = date.toFormat('yy');

    return `${month}/${year}`;
  } catch {
    return 'N/A';
  }
};
